<script setup>
import { computed, onMounted, ref } from 'vue';
import Auth from 'dashboard/api/auth';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  hasPushPermissions,
  requestPushPermissions,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

// Zimobi: the mobile bottom bar renders this menu inside a 64px row, where the
// full-width trigger and the name/email block do not fit. That bar also has no
// SidebarAccountSwitcher, so account switching moves in here — which is where
// the v3 OptionsMenu kept it.
const props = defineProps({
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openKeyShortcutModal', 'toggleAccounts']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// Zimobi: restore the manual push-subscription action that existed in the v3
// profile menu. It remains visible until this browser has a subscription.
const hasEnabledPushPermissions = ref(false);

const loadPushSubscriptionState = () => {
  verifyServiceWorkerExistence(registration =>
    registration.pushManager
      .getSubscription()
      .then(subscription => {
        hasEnabledPushPermissions.value = Boolean(subscription);
      })
      .catch(() => {
        // Zimobi: keep the recovery action available when subscription state
        // cannot be read instead of hiding the only way to retry.
        hasEnabledPushPermissions.value = false;
      })
  );
};

const onRequestPushPermissions = () => {
  requestPushPermissions({
    onSuccess: () => {
      hasEnabledPushPermissions.value = true;
    },
    onError: reason => {
      // Zimobi: a denied permission cannot be requested again by JavaScript;
      // direct the agent to the browser's site settings.
      if (reason === 'denied') {
        useAlert(
          t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION_BLOCKED')
        );
      }
    },
  });
};

onMounted(() => {
  if (hasPushPermissions()) loadPushSubscriptionState();
});

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      // Zimobi: compact (mobile) only — the sidebar reaches account switching
      // through SidebarAccountSwitcher instead.
      show: props.compact,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.CHANGE_ACCOUNTS'),
      icon: 'i-lucide-building-2',
      click: () => {
        emit('toggleAccounts');
      },
    },
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      // Zimobi: manual recovery path for a dismissed push prompt or a missing
      // browser subscription.
      show: !hasEnabledPushPermissions.value,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.ENABLE_PUSH_NOTIFICATIONS'),
      icon: 'i-lucide-bell',
      click: onRequestPushPermissions,
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://www.chatwoot.com/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer
    class="relative min-w-0"
    :class="{ 'w-full': !compact }"
    @close="emit('close')"
  >
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center rounded-lg cursor-pointer text-left hover:bg-n-alpha-1 p-1"
        :class="{ 'bg-n-alpha-1': isOpen, 'w-full': !compact }"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div v-if="!compact" class="min-w-0">
          <div class="text-n-slate-12 text-sm leading-4 font-medium truncate">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <!--
      Zimobi: DropdownContainer wraps the body in a bare `absolute` div with no inset, so that
      div sits at its static position -- the container's left edge -- and shrink-wraps to zero
      width because its only child is out of flow. `right-0` therefore pins the menu's right
      edge to where the trigger *starts*, which pushed the 320px menu 15px off-screen on a
      390px viewport. The bottom bar has no room to widen the trigger, so the compact menu
      positions against the viewport instead: `!fixed` is needed because the primitive
      hardcodes `absolute`, which wins on cascade order otherwise.
    -->
    <DropdownBody
      class="z-50 mb-2"
      :class="
        compact
          ? '!fixed bottom-16 inset-x-4 w-auto'
          : 'bottom-12 w-80 ltr:left-0 rtl:right-0'
      "
    >
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>
